import useTiclickTableConfig from '@shared/hooks/use-ticlick-table-config';
import { Button } from '@src/design-system/button';
import AddIcon from '@shared/assets/icons/shared/add-icon';
import { TABLES_KEYS } from '@shared/constants/tables-keys';
import type { ActionItemProps } from '@src/design-system/molecules/ticlick-table/ticlick-table-table-actions';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@src/design-system/dialog';
import { useHasPermission } from '@shared/hooks/use-has-permission';
import { entities } from '@shared/constants/entities';
import { BulkDeleteAction } from '@src/design-system/molecules/ticlick-table/actions';
import type { TAnnouncement } from '@src/shared/types/announcement';
import announcementsApi from '../services/announcements-api';
import { announcementsColumns } from '../tables/announcements-columns';
import AnnouncementsActionsCell from '../tables/announcements-actions-cell';
import { CreateOrEditAnnouncementDialog } from '../components/create-or-edit-announcement-dialog';

export function useAnnouncementsTable() {
  const { mutateAsync: bulkDelete, isPending: bulkDeletePending } =
    announcementsApi.useBulkDelete();
  const hasPermission = useHasPermission();
  const canAdd = hasPermission(entities.announcement, 'add');
  const canDelete = hasPermission(entities.announcement, 'delete');

  const actionsTable: ActionItemProps<TAnnouncement>[] = [
    {
      key: 'delete',
      render: (table) => {
        const ids = table.getSelectedRowModel().rows.map((r) => r.original.id);
        return (
          <BulkDeleteAction
            isLoading={bulkDeletePending}
            onConfirm={(closeModal) => {
              bulkDelete(
                { ids },
                {
                  onSuccess: () => {
                    table.resetRowSelection();
                    closeModal();
                  },
                }
              );
            }}
            entityLabel='announcements'
          />
        );
      },
      minWidth: 100,
      visibility: 'selection',
      isDisplay: canDelete,
    },
    {
      key: 'add',
      visibility: 'none-selection',
      minWidth: 160,
      render: () => (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant='primary'
              className='h-9 pr-5 text-sm font-medium rounded-xl'
            >
              <AddIcon />
              Add Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className='max-w-300 w-[95vw] p-0 overflow-hidden'>
            <CreateOrEditAnnouncementDialog />
          </DialogContent>
        </Dialog>
      ),
      isDisplay: canAdd,
    },
  ];

  const table = useTiclickTableConfig({
    options: { columns: announcementsColumns },
    identifyingData: {
      tableKey: TABLES_KEYS.announcementsTable,
    },
    fetchingData: {
      fetchFn: announcementsApi.useFetch,
    },
    rowActions: [
      {
        key: 'actions',
        render: (row) => <AnnouncementsActionsCell row={row} />,
        minWidth: 20,
      },
    ],
  });

  return {
    table,
    actionsTable,
  };
}
